import { isNative } from '../../native/native'

// On phones and tablets BOSS Chat runs only as the installed app (docs: "Installing on a phone"). A phone's browser gets the install page
// instead: installed, the app has its own storage, gets push on iOS, and a sign-in there is the one that counts. Desktop browsers are unchanged.

export type InstallPlatform = 'ios' | 'android'

/** Which kind of phone or tablet this is, or null for a computer. */
export function installPlatform(nav: Pick<Navigator, 'userAgent' | 'platform' | 'maxTouchPoints'> = navigator): InstallPlatform | null {
  const ua = nav.userAgent
  // iPadOS asks for desktop sites and says it is a Mac: a Mac with a touch screen is an iPad.
  if (/iPad|iPhone|iPod/.test(ua) || (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1)) return 'ios'
  if (/Android/i.test(ua)) return 'android'
  return null
}

/** Opened from the home screen icon rather than a browser tab. */
export function isInstalledApp(win: Pick<Window, 'matchMedia' | 'navigator'> = window): boolean {
  const nav = win.navigator as Navigator & { standalone?: boolean }
  if (nav.standalone === true) return true
  return ['standalone', 'fullscreen', 'minimal-ui'].some((mode) => win.matchMedia?.(`(display-mode: ${mode})`).matches === true)
}

export type IosBrowser = 'safari' | 'other' | 'in-app'

/** Safari, another browser (which can add to the Home Screen from iOS 16.4), or an app's own web view (which cannot). */
export function iosBrowser(ua = navigator.userAgent): IosBrowser {
  if (/FBAN|FBAV|Instagram|LinkedInApp|GSA\/|Line\/|MicroMessenger|Snapchat|Twitter/i.test(ua)) return 'in-app'
  if (/CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo|YaBrowser/i.test(ua)) return 'other'
  return 'safari'
}

export type AndroidBrowser = 'chrome' | 'samsung' | 'other' | 'in-app'

export function androidBrowser(ua = navigator.userAgent): AndroidBrowser {
  if (/; wv\)|FBAN|FBAV|Instagram|LinkedInApp|GSA\/|Line\/|MicroMessenger|Snapchat/i.test(ua)) return 'in-app'
  if (/SamsungBrowser/i.test(ua)) return 'samsung'
  if (/Chrome\//.test(ua) && !/Edg|OPR|Firefox/i.test(ua)) return 'chrome'
  return 'other'
}

/** Support and testing only: `?browser=1` lets this browser tab run the app anyway, until `?browser=0`. */
const BYPASS_KEY = 'boss-chat:allow-browser'

export function browserAllowed(search = location.search, storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null = safeStorage()): boolean {
  const flag = new URLSearchParams(search).get('browser')
  try {
    if (flag === '1') storage?.setItem(BYPASS_KEY, '1')
    if (flag === '0') storage?.removeItem(BYPASS_KEY)
    return storage?.getItem(BYPASS_KEY) === '1'
  } catch {
    return flag === '1'
  }
}

function safeStorage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

/** Whether to show the install page instead of the app. */
export function mustInstall(): boolean {
  // The BOSS Chat app is the installed app.
  if (isNative()) return false
  return installPlatform() !== null && !isInstalledApp() && !browserAllowed()
}

/** Chrome's install prompt (not in the DOM typings). */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferred: BeforeInstallPromptEvent | null = null
let installed = false
const listeners = new Set<() => void>()
const notify = () => listeners.forEach((l) => l())

/**
 * Chrome offers its install prompt once, early, as `beforeinstallprompt`: keep it for the Install button. Called before anything is drawn so the
 * event is not missed.
 */
export function captureInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferred = event as BeforeInstallPromptEvent
    notify()
  })
  window.addEventListener('appinstalled', () => {
    deferred = null
    installed = true
    notify()
  })
}

export function installPromptState(): { canPrompt: boolean; installed: boolean } {
  return { canPrompt: deferred !== null, installed }
}

export function onInstallPromptChange(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Shows Chrome's own install dialog. Resolves true when the person accepted. */
export async function promptInstall(): Promise<boolean> {
  const event = deferred
  if (!event) return false
  deferred = null
  await event.prompt()
  const { outcome } = await event.userChoice
  if (outcome === 'accepted') installed = true
  notify()
  return outcome === 'accepted'
}

/** When the page is opened as the installed app after all (Android can switch a tab into the app), start the app. */
export function reloadWhenInstalled(): void {
  const query = window.matchMedia?.('(display-mode: standalone)')
  query?.addEventListener?.('change', (e) => {
    if (e.matches) location.reload()
  })
}

/** Test seam. */
export function resetInstallPrompt(): void {
  deferred = null
  installed = false
}
